import React, { useCallback, useEffect, useState } from 'react';

import { FormHandles } from '@unform/core';
import { toast } from 'react-toastify';
import { UFs } from '../../../utils/StatesBR/UF';
import * as S from '../styles';
import * as C from './styles';
import {
  Input,
  InputRadio,
  Button,
  AutoSelectCreatable,
  InputMask,
} from '~/components';
import { IAutoSelectProps } from '~/models/Common';
import SeedsService from '~/services/Seeds';
import { ISingleFormLead } from '~/models/Auth';
import ViaCepService from '~/services/viaCep';
import { IAddress } from '~/models/viaCep';
import LimitInputField from '~/utils/functions/limitInputField';
import { formatToOptions } from '~/utils/functions';
import Select from '~/components/Select';
import InputCpfCnpjMask from '~/components/inputCPFCNPJ';

interface PreferencesProps {
  handleSubmit: (data: ISingleFormLead) => void;
  formRef: React.RefObject<FormHandles>;
  newOperatorOptions: string[];
  setNewOperatorOptions: (state: string[]) => void;
}

const Preferences: React.FC<PreferencesProps> = ({
  handleSubmit,
  formRef,
  newOperatorOptions,
  setNewOperatorOptions,
}) => {
  const [leadSend, setLeadSend] = useState<boolean>(false);
  const [answeredInputs, setAnsweredInputs] = useState(0);
  const [operatorOptions, setOperatorOptions] = useState<IAutoSelectProps[]>(
    [],
  );
  const [states, setStates] = useState<IAutoSelectProps[]>([]);
  // const whatsappOptions = [
  //   { id: 1, label: 'Sim', value: true },
  //   { id: 2, label: 'Não', value: false },
  // ];
  const [cepError, setCepError] = useState<boolean>(true);
  const [leadOptions, setLeadOptions] = useState<IAutoSelectProps[]>([]);
  const [infoCep, setInfoCep] = useState<IAddress>();
  const [documentType, setDocumentType] = useState<'cpf' | 'cnpj'>('cpf');
  let autocomplete: google.maps.places.Autocomplete;

  function gtag_report_conversion(): boolean {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore: Unreachable code error
    return false;
  }

  const loadCepInfo = useCallback(
    async (cep: string) => {
      try {
        if (cep.replace(/\D/g, '').length > 7) {
          const response = await ViaCepService.getAddress(cep);
          setInfoCep(response.data);
          setCepError(false);
          if (Object.keys(response.data).includes('erro')) {
            formRef.current?.setFieldValue('city', '');
            formRef.current?.setFieldValue('state', '');
            setCepError(true);
            toast.info('Não encontramos informações sobre seu cep');
          }
        } else {
          setCepError(true);
        }
      } catch (err) {
        toast.error('Houve um erro ao buscar seu cep');
      }
    },
    [formRef],
  );

  const fillInAddress = (): void => {
    formRef.current?.clearField('number');
    formRef.current?.clearField('street');
    formRef.current?.clearField('zipcode');
    formRef.current?.clearField('neighborhood');
    formRef.current?.clearField('city');
    formRef.current?.clearField('state');
    formRef.current?.clearField('complement');
    const place = autocomplete.getPlace();

    (
      place.address_components as google.maps.GeocoderAddressComponent[]
    ).forEach((component) => {
      const componentType = component.types[0];

      switch (componentType) {
        case 'street_number': {
          formRef.current?.setFieldValue('number', component.long_name);
          break;
        }
        case 'route': {
          formRef.current?.setFieldValue('street', component.long_name);
          break;
        }
        case 'postal_code': {
          formRef.current?.setFieldValue('zipcode', component.long_name);
          break;
        }
        case 'sublocality_level_1':
        case 'sublocality_level_2': {
          formRef.current?.setFieldValue('neighborhood', component.long_name);
          break;
        }
        case 'administrative_area_level_4':
        case 'administrative_area_level_3':
        case 'administrative_area_level_2': {
          formRef.current?.setFieldValue('city', component.long_name);
          break;
        }
        case 'administrative_area_level_1': {
          formRef.current?.setFieldValue('state', component.long_name);
          break;
        }
        default: {
          break;
        }
      }
    });
  };

  useEffect(() => {
    const addressField = document.getElementById(
      'autocomplete',
    ) as HTMLInputElement;
    if (addressField && window.google && window.google.maps) {
      autocomplete = new window.google.maps.places.Autocomplete(addressField, {
        componentRestrictions: { country: ['br'] },
        fields: ['address_components', 'geometry'],
        types: ['address'],
      });

      autocomplete.addListener('place_changed', fillInAddress);
    }
    // eslint-disable-next-line
  }, [fillInAddress]);

  useEffect(() => {
    const getOperators = async (): Promise<void> => {
      const response = await SeedsService.getOperators();

      const optionsForInput = response.data
        .filter((operator: any) => operator.status)
        .map((operator: any) => ({
          id: operator.id,
          value: operator.id,
          label: operator.name,
        }));
      setOperatorOptions(optionsForInput);
    };

    getOperators();

    const formatedStates = formatToOptions(UFs, 'sigla', 'sigla');
    setStates(formatedStates);
  }, []);

  useEffect(() => {
    const getLeadTypes = async (): Promise<void> => {
      const response = await SeedsService.getLeadTypes();
      const optionsForInput = response.data.map((option: any) => ({
        id: option.id,
        value: option.id,
        label: option.name,
      }));

      setLeadOptions(optionsForInput);
    };

    getLeadTypes();
  }, []);

  const handleChangeUF = (value: string): void => {
    const newValue = LimitInputField(value, 2);
    formRef.current?.setFieldValue('state', newValue);
  };

  const handleShowInputs = useCallback(
    (inputNumber: number): void => {
      if (inputNumber >= answeredInputs) {
        setAnsweredInputs(inputNumber);
      }
    },
    [answeredInputs],
  );

  useEffect(() => {
    if (infoCep) {
      formRef.current?.setFieldValue('city', infoCep?.localidade);
      formRef.current?.setFieldValue('state', infoCep?.uf);
    }
  }, [infoCep, formRef]);

  const validateCpfCnpj = useCallback((): boolean => {
    const cpf = formRef.current?.getFieldValue('cpf');
    const cnpj = formRef.current?.getFieldValue('cnpj');
    if (!cpf && !cnpj) {
      toast.error('Informe CPF ou CNPJ.');
      return false;
    }
    return true;
  }, [formRef]);

  // Handler de envio do formulário
  const handleFinish = (): void => {
    if (validateCpfCnpj()) {
      if (!leadSend) {
        gtag_report_conversion();
        formRef.current?.submitForm();
        setLeadSend(true);
      }
    } else {
      toast.error('Informe CPF ou CNPJ.');
    }
  };

  // Torna o valor inicial "cpf" ao montar
  useEffect(() => {
    formRef.current?.setFieldValue('documentType', 'cpf');
    setDocumentType('cpf');
  }, [formRef]);

  // Função para receber atualizações direto do input de CPF/CNPJ
  const handleCpfCnpjChange = useCallback(
    (numbers: string) => {
      // Sempre limpa ambos antes, depois seta o correto
      formRef.current?.setFieldValue('cpf', '');
      formRef.current?.setFieldValue('cnpj', '');

      if (numbers.length > 11) {
        formRef.current?.setFieldValue('cnpj', numbers.slice(0, 14));
      } else if (numbers.length > 0) {
        formRef.current?.setFieldValue('cpf', numbers.slice(0, 11));
      }
    },
    [formRef],
  );

  const handleSubmitWithWhatsapp = (data: ISingleFormLead): void => {
    const payload = {
      ...data,
      contact_by_whatsapp: true,
    };
    handleSubmit(payload);
  };

  return (
    <S.FormCard ref={formRef} onSubmit={handleSubmitWithWhatsapp}>
      <S.InputWrapper>
        <S.Label>Nome do contato ou empresa</S.Label>
        <Input name="name" />
      </S.InputWrapper>

      <S.InputWrapper>
        <S.Label>CPF ou CNPJ</S.Label>

        <InputCpfCnpjMask
          name="cpf_cnpj"
          formRef={formRef}
          onChangeNumerico={handleCpfCnpjChange}
        />
      </S.InputWrapper>

      <S.InputWrapper style={{ display: 'none' }}>
        <Input name="cpf" />
        <Input name="cnpj" />
      </S.InputWrapper>

      {/* <S.InputWrapper>
        <S.Label>Selecione CPF ou CNPJ</S.Label>
        <InputRadio
          row
          name="documentType"
          options={[
            { id: 'cpf', label: 'CPF', value: 'cpf' },
            { id: 'cnpj', label: 'CNPJ', value: 'cnpj' },
          ]}
          onChangeCallback={() => {
            const selectedDocType =
              formRef.current?.getFieldValue('documentType');
            setDocumentType(selectedDocType || '');
            if (selectedDocType === 'cpf') {
              formRef.current?.setFieldValue('cnpj', '');
            } else if (selectedDocType === 'cnpj') {
              formRef.current?.setFieldValue('cpf', '');
            }
          }}
        />
      </S.InputWrapper>

      {documentType === 'cpf' && (
        <S.InputWrapper>
          <S.Label>CPF</S.Label>
          <InputMask
            id="cpf"
            name="cpf"
            placeholder="CPF"
            mask="999.999.999-99"
            onChange={() => {
              validateCpfCnpj();
            }}
          />
        </S.InputWrapper>
      )}

      {documentType === 'cnpj' && (
        <S.InputWrapper>
          <S.Label>CNPJ</S.Label>
          <InputMask
            id="cnpj"
            name="cnpj"
            placeholder="CNPJ"
            mask="99.999.999/9999-99"
            onChange={() => {
              validateCpfCnpj();
            }}
          />
        </S.InputWrapper>
      )} */}

      <S.InputWrapper>
        <S.Label>CEP</S.Label>
        <InputMask
          id="zipcode"
          name="zipcode"
          placeholder="CEP"
          type="tel"
          mask="99999-999"
          onChange={(value) => loadCepInfo(value.target.value)}
        />
      </S.InputWrapper>

      <S.InputWrapper>
        <S.Label>UF</S.Label>
        <Select name="state" options={states} />
      </S.InputWrapper>
      <S.InputWrapper>
        <S.Label>Cidade</S.Label>
        <Input name="city" placeholder="Cidade" disabled={cepError} />
      </S.InputWrapper>
      <S.InputWrapper>
        <AutoSelectCreatable
          name="select_operators"
          options={operatorOptions}
          setOptions={setOperatorOptions}
          newOptions={newOperatorOptions}
          setNewOptions={setNewOperatorOptions}
          placeholder="Escolha um ou mais planos de saúde para atendimento."
          onChangeCallback={() => {
            handleShowInputs(2);
          }}
          style={{ marginBottom: '0.5rem' }}
        />
      </S.InputWrapper>
      <S.InputWrapper>
        <S.Label>Telefone</S.Label>
        <InputMask name="phone" type="tell" mask="(99) 9999-9999" />
      </S.InputWrapper>
      {/* <S.InputWrapper>
        <S.Label>Deseja ser contatado pelo WhatsApp?</S.Label>
        <InputRadio name="contact_by_whatsapp" options={whatsappOptions} />
      </S.InputWrapper> */}
      <S.InputWrapper>
        <S.Label>Email</S.Label>
        <Input name="email" type="email" />
      </S.InputWrapper>
      <S.InputWrapper>
        {leadOptions.length > 0 && (
          <InputRadio
            name="lead_type_id"
            options={leadOptions}
            onChangeCallback={() => {
              handleShowInputs(6);
            }}
          />
        )}
      </S.InputWrapper>
      <C.ButtonWrapper>
        <Button
          id="submit-form-finished"
          containerStyle={{ maxWidth: 200, boxShadow: 'none' }}
          textStyle={{ fontWeight: 400 }}
          onClick={handleFinish}
        >
          Concluir
        </Button>
      </C.ButtonWrapper>
    </S.FormCard>
  );
};

export default Preferences;
